import { type AnalyticsEvent, trackAnalyticsEvent } from '@autone/analytics';
import { type MouseEvent, useCallback, useMemo } from 'react';

import { Checkbox, type CheckboxProps } from '../../atoms';

import { usePaginatedSelectionContext } from './hooks/use-paginated-selection/usePaginatedSelection.context';

interface DataTableSelectAllCheckboxProps
  extends Omit<CheckboxProps, 'checked' | 'onCheckedChange'> {
  trackingEvent?: AnalyticsEvent;
}

export const DataTableSelectAllCheckbox = ({
  trackingEvent,
  ...props
}: DataTableSelectAllCheckboxProps) => {
  const [{ getIsAllRowsSelected }, { changeMode, clearSelection }] =
    usePaginatedSelectionContext();

  const allRowsSelected = getIsAllRowsSelected();
  const checkedState = useMemo(() => {
    if (allRowsSelected === 'all') return true;
    if (allRowsSelected === 'some') return 'indeterminate';
    return false;
  }, [allRowsSelected]);

  const handleClick = useCallback(() => {
    if (trackingEvent) {
      trackAnalyticsEvent(trackingEvent);
    }
    if (allRowsSelected === 'none') {
      changeMode('bulk');
    } else {
      clearSelection();
    }
  }, [allRowsSelected, changeMode, clearSelection, trackingEvent]);

  return <Checkbox {...props} checked={checkedState} onClick={handleClick} />;
};

interface DataTableRowCheckboxProps
  extends Omit<CheckboxProps, 'checked' | 'onCheckedChange'> {
  rowId: string;
  trackingEvent?: AnalyticsEvent;
}

export const DataTableRowCheckbox = ({
  rowId,
  trackingEvent,
  ...props
}: DataTableRowCheckboxProps) => {
  const [{ getIsRowSelected }, { toggleRow, toggleRange }] =
    usePaginatedSelectionContext();

  const checkedState = useMemo(
    () => getIsRowSelected(rowId),
    [getIsRowSelected, rowId],
  );

  const handleClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (trackingEvent) {
        trackAnalyticsEvent(trackingEvent);
      }
      if (e.shiftKey) {
        toggleRange(rowId);
      } else {
        toggleRow(rowId);
      }
    },
    [toggleRow, toggleRange, rowId, trackingEvent],
  );

  return <Checkbox {...props} checked={checkedState} onClick={handleClick} />;
};
